import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Alert from '@material-ui/core/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import MicIcon from '@material-ui/icons/Mic';
import GroupIcon from '@material-ui/icons/Group';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import Container from 'common/Container';
import { registerPressStartUser } from '../../../services/userService';
import { sendEmail } from '../../../services/emailService';

/** Marca TNOCON: TNO 900 + sombra muy fina; CON ligero, tracking cerrado y pegado a TNO. */
const BrandTnoCon = ({ onDark = false, sxTno = {}, sxCon = {} }) => (
  <Box component="span" sx={{ whiteSpace: 'nowrap', display: 'inline' }}>
    <Box
      component="span"
      sx={{
        fontWeight: 900,
        letterSpacing: '-0.07em',
        textShadow:
          '0.02em 0 0 currentColor, -0.02em 0 0 currentColor, 0 0.02em 0 currentColor, 0 -0.02em 0 currentColor',
        ...sxTno,
      }}
    >
      TNO
    </Box>
    <Box
      component="span"
      sx={{
        fontWeight: 200,
        letterSpacing: '-0.07em',
        marginLeft: '-0.06em',
        color: onDark ? 'rgba(255,255,255,0.72)' : '#4a505c',
        ...sxCon,
      }}
    >
      CON
    </Box>
  </Box>
);

const BRAND_TNOCON_HTML =
  '<span style="font-weight:900;letter-spacing:-0.07em;color:#e8f7ff;text-shadow:0.02em 0 0 #e8f7ff,-0.02em 0 0 #e8f7ff,0 0.02em 0 #e8f7ff,0 -0.02em 0 #e8f7ff">TNO</span><span style="font-weight:200;letter-spacing:-0.07em;margin-left:-0.06em;color:#c8d8f0">CON</span>';

const eventHighlights = [
  {
    title: 'Keynote "Señal 2026"',
    icon: MicIcon,
    description:
      'Presentación de la nueva parrilla de programación y los hitos del ecosistema digital: señal en vivo, web y On-Demand.',
  },
  {
    title: 'The Talent Line-Up',
    icon: GroupIcon,
    description:
      'Encuentro con los rostros de nuestros programas en vivo y especialistas de referencia.',
  },
  {
    title: 'Media Lab Showcase',
    icon: VideoLibraryIcon,
    description:
      'Un vistazo exclusivo a la cocina de nuestras producciones On-Demand y cómo la tecnología impulsa nuestro contenido.',
  },
  {
    title: 'Data & Reach',
    icon: EqualizerIcon,
    description:
      'Revisión de impacto con +90.000 seguidores totales, crecimiento en web y cómo la conversación se extiende más allá del estudio.',
  },
];

const mediaTypes = ['Radio', 'Web / Digital', 'Impreso', 'Streaming / Podcast', 'Otros'];
const EVENT_DATE = new Date('2026-04-21T10:00:00-04:00');
const PRESS_FROM_EMAIL = process.env.REACT_APP_PRESSSTART_FROM_EMAIL || 'mercadeoyventas@tnonetwork.com';
const PRESS_NOTIFY_EMAIL = process.env.REACT_APP_PRESSSTART_NOTIFY_EMAIL || 'mercadeoyventas@tnonetwork.com';
const PRESS_NOTIFY_CC_EMAIL =
  process.env.REACT_APP_PRESSSTART_NOTIFY_CC || 'solvargasoficial@gmail.com';
const EVENT_MAPS_URL =
  process.env.REACT_APP_PRESSSTART_MAPS_URL || 'https://share.google/W53TGyHp3m8uSLD5y';

/** Logística de estacionamiento junto al recinto. */
const EVENT_PARKING_NOTE =
  'Quienes asistan en vehículo propio pueden estacionar en el Centro Comercial Paseo Las Mercedes.';

// URL absoluta para correo; usar ads.tnonetwork.com (certificado válido). tnocon no está en el SAN del cert actual.
const EMAIL_HEADER_LOGO_PATH = '/images/logos/logo_white.PNG';
const EMAIL_HEADER_LOGO_URL = (() => {
  if (process.env.REACT_APP_PRESSSTART_EMAIL_LOGO_URL) {
    return process.env.REACT_APP_PRESSSTART_EMAIL_LOGO_URL;
  }
  const site = (process.env.REACT_APP_SITE_URL || '').replace(/\/$/, '');
  if (site) return `${site}${EMAIL_HEADER_LOGO_PATH}`;
  const pub = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  if (pub.startsWith('http')) return `${pub}${EMAIL_HEADER_LOGO_PATH}`;
  return `https://ads.tnonetwork.com${EMAIL_HEADER_LOGO_PATH}`;
})();

const buildEmailShell = ({ title, subtitle, content, footer }) => `
  <div style="margin:0;padding:0;background:#ffffff;font-family:Arial,Helvetica,sans-serif;">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#ffffff;padding:24px 12px;">
      <tr>
        <td align="center">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:680px;background:#355a8c;border:1px solid #c8d4e8;border-radius:16px;overflow:hidden;">
            <tr>
              <td style="padding:22px 24px;background:linear-gradient(130deg,#2ec8ff 0%, #3d6eb8 42%, #355a8c 100%);">
                <img src="${EMAIL_HEADER_LOGO_URL}" alt="TNO Network" width="200" border="0" style="display:block;border:0;outline:none;text-decoration:none;margin-bottom:14px;max-width:200px;height:auto;" />
                <div style="font-size:12px;letter-spacing:.7px;color:#e8f7ff;">${BRAND_TNOCON_HTML}<span style="font-weight:700"> 2026 | PRESS START</span></div>
                <h1 style="margin:8px 0 0 0;color:#ffffff;font-size:26px;line-height:1.2;">${title}</h1>
                <p style="margin:8px 0 0 0;color:#e8eef8;font-size:14px;line-height:1.5;">${subtitle}</p>
              </td>
            </tr>
            <tr>
              <td style="padding:24px;background:#3d6eb8;color:#ffffff;">
                ${content}
              </td>
            </tr>
            <tr>
              <td style="padding:16px 24px;border-top:1px solid rgba(255,255,255,.25);background:#2d5080;color:#c8d8f0;font-size:12px;line-height:1.6;">
                ${footer}
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </div>
`;

const buildAdminEmailHtml = (submitted, technical, interview, sourceUrl, timestamp) =>
  buildEmailShell({
    title: 'Nueva solicitud de acreditación',
    subtitle: `Se registró un medio para ${BRAND_TNOCON_HTML} &quot;Press Start&quot;.`,
    content: `
      <p style="margin:0 0 16px 0;font-size:14px;color:#ffffff;">Revisa los datos del solicitante:</p>
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-collapse:collapse;">
        ${[
    ['Nombre', submitted.fullName],
    ['Cédula / ID', submitted.idNumber],
    ['Teléfono', submitted.phone],
    ['Correo', submitted.email],
    ['Medio', submitted.mediaName],
    ['Tipo de medio', submitted.mediaType],
    ['Cargo / Función', submitted.role],
    ['Web / Redes', submitted.mediaLink || '—'],
    ['Equipo técnico', technical],
    ['Solicita entrevista', interview],
    ['Talento / programa', submitted.interviewTarget || '—'],
    ['URL de registro', sourceUrl || '—'],
    ['Fecha', timestamp],
  ].map(([label, value]) => `
          <tr>
            <td style="padding:9px 10px;border:1px solid rgba(255,255,255,.28);font-size:13px;color:#d0ddf5;width:34%;"><strong>${label}</strong></td>
            <td style="padding:9px 10px;border:1px solid rgba(255,255,255,.28);font-size:13px;color:#ffffff;">${value}</td>
          </tr>
        `).join('')}
      </table>
    `,
    footer: 'Equipo TNO Network · Notificación automática de acreditaciones Press Start.',
  });

const getCountdownParts = (targetDate) => {
  const now = new Date();
  const diffMs = Math.max(targetDate.getTime() - now.getTime(), 0);
  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds };
};

const LandingPressStart = () => {
  const [form, setForm] = useState({
    fullName: '',
    idNumber: '',
    phone: '',
    email: '',
    mediaName: '',
    mediaType: '',
    role: '',
    mediaLink: '',
    hasTechnicalEquipment: '',
    wantsInterview: '',
    interviewTarget: '',
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: null, message: '' });
  const [countdown, setCountdown] = useState(() => getCountdownParts(EVENT_DATE));

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(getCountdownParts(EVENT_DATE));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (status.type) setStatus({ type: null, message: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: null, message: '' });
    const result = await registerPressStartUser({
      ...form,
      hasTechnicalEquipment: form.hasTechnicalEquipment === 'Si',
      wantsInterview: form.wantsInterview === 'Si',
    });
    if (!result.success) {
      setLoading(false);
      setStatus({
        type: 'error',
        message: result.error || 'No se pudo guardar el registro. Intenta de nuevo.',
      });
      return;
    }

    const submitted = { ...form };
    const technical = submitted.hasTechnicalEquipment === 'Si' ? 'Sí' : 'No';
    const interview = submitted.wantsInterview === 'Si' ? 'Sí' : 'No';
    const sourceUrl = typeof window !== 'undefined' ? window.location.href : '';
    const timestamp = new Date().toISOString();

    try {
      await sendEmail({
        to: [PRESS_NOTIFY_EMAIL],
        cc: [PRESS_NOTIFY_CC_EMAIL],
        from: PRESS_FROM_EMAIL,
        replyTo: PRESS_FROM_EMAIL,
        subject: `Nueva acreditación de prensa | TNOCON Press Start`,
        text:
          `Nueva solicitud de acreditación:\n\n` +
          `Nombre: ${submitted.fullName}\n` +
          `Cédula/ID: ${submitted.idNumber}\n` +
          `Teléfono: ${submitted.phone}\n` +
          `Correo: ${submitted.email}\n` +
          `Medio: ${submitted.mediaName}\n` +
          `Tipo de medio: ${submitted.mediaType}\n` +
          `Cargo/Función: ${submitted.role}\n` +
          `Web/Redes: ${submitted.mediaLink || '—'}\n` +
          `Equipo técnico: ${technical}\n` +
          `Solicita entrevista: ${interview}\n` +
          `Talento/programa: ${submitted.interviewTarget || '—'}\n` +
          `URL de registro: ${sourceUrl || '—'}\n` +
          `Fecha: ${timestamp}\n`,
        html: buildAdminEmailHtml(submitted, technical, interview, sourceUrl, timestamp),
      });
    } catch (err) {
      console.error('[PressStart] Error enviando correo interno:', err);
    }

    // Correo de bienvenida al acreditado: lo envía tnoradio-user-service tras POST /api/users/save
    // (SMTP MAIL_* en el servidor), no el navegador vía api.email — mejor entrega a cuentas externas.

    setLoading(false);
    setStatus({
      type: 'success',
      message:
        "Registro recibido. Tu solicitud de acreditación para TNOCON 'Press Start' está en proceso. En breve recibirás un correo con los detalles finales (revisa spam).",
    });
    setForm({
      fullName: '',
      idNumber: '',
      phone: '',
      email: '',
      mediaName: '',
      mediaType: '',
      role: '',
      mediaLink: '',
      hasTechnicalEquipment: '',
      wantsInterview: '',
      interviewTarget: '',
    });
  };

  return (
    <Box>
      <Box
        minHeight={{ xs: 'auto', md: 'calc(100vh - 64px)' }}
        position={'relative'}
        sx={{
          background:
            'linear-gradient(130deg, rgba(6,11,33,0.95) 0%, rgba(21,32,83,0.95) 48%, rgba(0,187,255,0.78) 100%)',
          color: 'common.white',
          overflow: 'hidden',
          '&:before': {
            content: '" "',
            position: 'absolute',
            top: -120,
            right: -120,
            width: 340,
            height: 340,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,247,255,0.45), rgba(0,247,255,0))',
          },
        }}
      >
        <Container>
          <Box py={{ xs: 6, md: 10 }} position={'relative'} zIndex={2}>
            <Chip
              label={(
                <span>
                  <BrandTnoCon onDark />
                  <Box component="span" sx={{ fontWeight: 700 }}> 2026 | PRESS START</Box>
                </span>
              )}
              sx={{
                mb: 3,
                color: 'common.white',
                bgcolor: 'rgba(255,255,255,0.12)',
              }}
            />
            <Box
              width={{ xs: 340, md: 520 }}
              height={{ xs: 104, md: 156 }}
              sx={{
                mb: 3,
                backgroundImage: 'url(/images/logos/logo_white.PNG)',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                backgroundPosition: 'left center',
              }}
            />
            <Typography variant={'h2'} sx={{ fontWeight: 800 }} gutterBottom>
              Acreditación de Prensa
            </Typography>
            <Typography variant={'h5'} sx={{ maxWidth: 860, opacity: 0.95 }} paragraph>
              Bienvenido al registro oficial para <BrandTnoCon onDark /> &quot;Press Start&quot;, la jornada exclusiva para medios de comunicación
              dentro del marco de nuestra gran convención anual.
            </Typography>
            <Typography variant={'h6'} sx={{ maxWidth: 860, opacity: 0.95 }} paragraph>
              Martes, 21 de abril de 2026 | 10:00 a. m. | Edif. Manzanares, Piso 2, Las Mercedes.
            </Typography>
            <Link
              href={EVENT_MAPS_URL}
              target="_blank"
              rel="noreferrer"
              underline="hover"
              sx={{ color: 'common.white', fontWeight: 700, fontSize: '1rem' }}
            >
              Ver ubicación en Google Maps
            </Link>
            <Box mt={4}>
              <Card
                sx={{
                  p: { xs: 2, md: 3 },
                  backgroundColor: 'rgba(255,255,255,0.10)',
                  border: '1px solid rgba(255,255,255,0.22)',
                  maxWidth: 560,
                }}
              >
                <Typography variant={'subtitle1'} sx={{ fontWeight: 700, mb: 2, color: 'common.white' }}>
                  Cuenta regresiva: modo hype activado
                </Typography>
                <Typography variant={'body2'} sx={{ mb: 2, color: 'rgba(255,255,255,0.92)' }}>
                  Ajusta tu acreditación, carga baterías y prepara el titular. 📸
                </Typography>
                <Grid container spacing={1.5}>
                  {[
                    { label: 'Días', value: countdown.days },
                    { label: 'Horas', value: countdown.hours },
                    { label: 'Minutos', value: countdown.minutes },
                    { label: 'Segundos', value: countdown.seconds },
                  ].map((item) => (
                    <Grid item xs={3} key={item.label}>
                      <Box textAlign={'center'}>
                        <Typography variant={'h4'} sx={{ fontWeight: 900, color: 'common.white' }}>
                          {String(item.value).padStart(2, '0')}
                        </Typography>
                        <Typography variant={'caption'} sx={{ color: 'rgba(255,255,255,0.92)' }}>
                          {item.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container>
        <Box py={6}>
          <Card variant={'outlined'} sx={{ p: { xs: 2.5, md: 4 }, mb: 4 }}>
            <Typography variant={'h4'} gutterBottom sx={{ fontWeight: 800 }} component="div">
              ¿Qué es la <BrandTnoCon />?
            </Typography>
            <Typography variant={'body1'} color={'textSecondary'} sx={{ lineHeight: 1.9 }}>
              Es la convención anual definitiva de TNO Network. Un espacio de tres días donde convergen nuestros
              productores, la prensa y las marcas para experimentar la evolución del <strong>ecosistema TNO</strong> —donde la
              radio visual convive con lo digital, las marcas y la innovación. En esta, nuestra primera edición, abrimos las
              puertas de nuestro universo para mostrar la tecnología, el talento y el contenido que nos consolidan como señal
              multiplataforma referente de la región.
            </Typography>
          </Card>

          <Typography variant={'h3'} gutterBottom sx={{ fontWeight: 700 }}>
            ¿Qué esperar en Press Start?
          </Typography>
          <Grid container spacing={3}>
            {eventHighlights.map((item) => (
              <Grid item xs={12} md={6} key={item.title}>
                <Card variant={'outlined'} sx={{ p: 3, height: '100%' }}>
                  <Box
                    width={44}
                    height={44}
                    borderRadius={'50%'}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    mb={1.5}
                    sx={{ backgroundColor: 'primary.main', color: 'common.white' }}
                  >
                    <item.icon />
                  </Box>
                  <Typography variant={'h5'} gutterBottom sx={{ fontWeight: 700 }}>
                    {item.title}
                  </Typography>
                  <Typography color={'textSecondary'}>{item.description}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      <Box sx={{ backgroundColor: 'alternate.main' }}>
        <Container>
          <Box py={6}>
            <Typography variant={'h3'} gutterBottom sx={{ fontWeight: 700 }}>
              Coordenadas del evento
            </Typography>
            <Typography variant={'h6'} paragraph component="div">
              Evento: <BrandTnoCon /> &quot;Press Start&quot;
            </Typography>
            <Typography paragraph>
              Fecha: Martes, 21 de abril de 2026.
              <br />
              Hora: 10:00 a. m.
              <br />
              Lugar: Final Av. Principal de Las Mercedes, Edif. Manzanares, Piso 2 (lateral al C.C. Paseo Las Mercedes).
            </Typography>
            <Typography paragraph color={'textSecondary'} sx={{ lineHeight: 1.8 }}>
              <Box component={'span'} sx={{ fontWeight: 600, color: 'text.primary' }}>
                Estacionamiento.{' '}
              </Box>
              {EVENT_PARKING_NOTE}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              href={EVENT_MAPS_URL}
              target="_blank"
              rel="noreferrer"
              sx={{ mb: 2 }}
            >
              Abrir en Google Maps
            </Button>
            <Typography color={'textSecondary'}>
              Para asegurar su Digital Press Kit y su credencial oficial de la convención, complete este formulario.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container>
        <Box py={6}>
          <Typography variant={'h3'} align={'center'} sx={{ fontWeight: 700 }} gutterBottom>
            Formulario de acreditación
          </Typography>
          <Typography align={'center'} color={'textSecondary'} paragraph>
            Complete sus datos para gestionar su credencial oficial y el kit digital.
          </Typography>

          <Box component={'form'} maxWidth={920} mx={'auto'} onSubmit={handleSubmit}>
            <Card variant={'outlined'} sx={{ p: { xs: 2, md: 4 } }}>
              {status.type === 'success' && (
                <Alert severity={'success'} sx={{ mb: 2 }} onClose={() => setStatus({ type: null, message: '' })}>
                  {status.message}
                </Alert>
              )}
              {status.type === 'warning' && (
                <Alert severity={'warning'} sx={{ mb: 2 }} onClose={() => setStatus({ type: null, message: '' })}>
                  {status.message}
                </Alert>
              )}
              {status.type === 'error' && (
                <Alert severity={'error'} sx={{ mb: 2 }} onClose={() => setStatus({ type: null, message: '' })}>
                  {status.message}
                </Alert>
              )}
              <Typography variant={'h5'} sx={{ fontWeight: 700 }} gutterBottom>
                Sección 1: Datos Personales
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField label={'Nombre y Apellido'} required fullWidth value={form.fullName} onChange={handleChange('fullName')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label={'Numero de Cedula / ID'} required fullWidth value={form.idNumber} onChange={handleChange('idNumber')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label={'Telefono de Contacto'} required fullWidth value={form.phone} onChange={handleChange('phone')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label={'Correo Electrónico'} type={'email'} required fullWidth value={form.email} onChange={handleChange('email')} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant={'h5'} sx={{ fontWeight: 700 }} gutterBottom>
                Sección 2: Información del Medio
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField label={'Nombre del Medio de Comunicación'} required fullWidth value={form.mediaName} onChange={handleChange('mediaName')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label={'Tipo de Medio'}
                    required
                    value={form.mediaType}
                    onChange={handleChange('mediaType')}
                    fullWidth
                  >
                    {mediaTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label={'Cargo / Función'} required fullWidth value={form.role} onChange={handleChange('role')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label={'Enlace a Web o Redes Sociales'} fullWidth value={form.mediaLink} onChange={handleChange('mediaLink')} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant={'h5'} sx={{ fontWeight: 700 }} gutterBottom>
                Sección 3: Requerimientos Técnicos
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label={'¿Asistirá con equipo técnico (cámaras, trípodes)?'}
                    required
                    value={form.hasTechnicalEquipment}
                    onChange={handleChange('hasTechnicalEquipment')}
                    fullWidth
                  >
                    <MenuItem value={'Si'}>Si</MenuItem>
                    <MenuItem value={'No'}>No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label={'¿Desea coordinar una entrevista individual?'}
                    required
                    value={form.wantsInterview}
                    onChange={handleChange('wantsInterview')}
                    fullWidth
                  >
                    <MenuItem value={'Si'}>Si</MenuItem>
                    <MenuItem value={'No'}>No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={'Talento o Programa de interés para entrevista'}
                    fullWidth
                    value={form.interviewTarget}
                    onChange={handleChange('interviewTarget')}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 4 }}>
                <Button
                  variant={'contained'}
                  color={'primary'}
                  size={'large'}
                  type={'submit'}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color={'inherit'} /> : null}
                >
                  {loading ? 'Enviando...' : 'Enviar acreditación'}
                </Button>
              </Box>

            </Card>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LandingPressStart;
